import type { ReactElement } from 'react';

import { CaretDownIcon, CaretUpIcon } from '@phosphor-icons/react';
import { useCallback, useState, useEffect, useId, useRef } from 'react';
import { useSortBy } from 'react-instantsearch';

import { S } from './FilterMenuStyles';
import { Skeleton } from '~/components/shared/common/SkeletonPlaceholder';
import { useDataLayerContext } from '~/utilities/context/static/DataLayerContext';
import { useProductListContext } from '~/utilities/context/static/ProductListContext';
import { useOuterClick, useOuterEnterKeyPress } from '~/utilities/hooks';
import { useAlgoliaSortingOptions } from '~/utilities/hooks/search/useAlgoliaSortingOptions';
import { parseSortingOptionsDataLayer } from '~/utilities/vendors';

type SortingDropdownProps = {
  isFirstLoading: boolean;
};

type DropdownArrowKey = 'ArrowDown' | 'ArrowUp';

const focusAdjacentDropdownItem = (
  dropdown: HTMLDivElement | null,
  key: DropdownArrowKey
): void => {
  const items = Array.from(
    dropdown?.querySelectorAll<HTMLButtonElement>('button[role="option"]') || []
  );

  if (items.length === 0) {
    return;
  }

  const currentIndex = items.indexOf(document.activeElement as HTMLButtonElement);
  let nextIndex: number;

  if (key === 'ArrowDown') {
    nextIndex = currentIndex < items.length - 1 ? currentIndex + 1 : 0;
  } else {
    nextIndex = currentIndex > 0 ? currentIndex - 1 : items.length - 1;
  }

  items[nextIndex]?.focus();
};

export const SortingDropdown = ({ isFirstLoading }: SortingDropdownProps): ReactElement => {
  const { systemTexts } = useProductListContext();
  const { sortingOptions, primaryIndex } = useAlgoliaSortingOptions();
  const { currentRefinement, refine: refineSortBy } = useSortBy({ items: sortingOptions });
  const { pushToDataLayer } = useDataLayerContext();
  const { ref, outerClicked, setOuterClicked } = useOuterClick();
  const { ref: keyRef, outerEnterKeyPressed, setOuterEnterKeyPressed } = useOuterEnterKeyPress();
  const [dropdownOpened, setDropdownOpened] = useState(false);
  const selectBoxRef = useRef<HTMLButtonElement | null>(null);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const dropdownId = useId();

  const selectedSortOption = sortingOptions.find(option => option.value === currentRefinement);
  const sortByLabel = systemTexts?.filterAndSort?.sortByLabel || '';

  const handleSortingOptionClick = useCallback(
    (value: string, labelEN: string) => {
      refineSortBy(value);
      pushToDataLayer(parseSortingOptionsDataLayer(labelEN));
      setDropdownOpened(false);
      selectBoxRef.current?.focus();
    },
    [refineSortBy, pushToDataLayer]
  );

  useEffect(() => {
    if (dropdownOpened) {
      const activeItem =
        dropdownRef.current?.querySelector<HTMLElement>('[aria-selected="true"]') ||
        dropdownRef.current?.querySelector<HTMLElement>('button');

      activeItem?.focus();
    }
  }, [dropdownOpened]);

  useEffect(() => {
    if (outerClicked || outerEnterKeyPressed) {
      setDropdownOpened(false);
    }
  }, [outerClicked, outerEnterKeyPressed]);

  const handleSelectBoxKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLButtonElement>) => {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        setDropdownOpened(true);
        setOuterClicked(false);
        setOuterEnterKeyPressed(false);
      }
    },
    [setOuterClicked, setOuterEnterKeyPressed]
  );

  const handleDropdownKeyDown = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      setDropdownOpened(false);
      selectBoxRef.current?.focus();

      return;
    }

    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      focusAdjacentDropdownItem(dropdownRef.current, e.key);

      return;
    }

    if (e.key === 'Home') {
      e.preventDefault();

      const items =
        dropdownRef.current?.querySelectorAll<HTMLButtonElement>('button[role="option"]');

      items?.[0]?.focus();

      return;
    }

    if (e.key === 'End') {
      e.preventDefault();

      const items =
        dropdownRef.current?.querySelectorAll<HTMLButtonElement>('button[role="option"]');

      items?.[items.length - 1]?.focus();

      return;
    }

    if (e.key === 'Tab') {
      setDropdownOpened(false);
    }
  }, []);

  return (
    <Skeleton when={isFirstLoading} width="100px" height="26px">
      <S.SortingOptionWrapper ref={keyRef}>
        <S.ItemWrapper ref={ref}>
          <S.SortBySelectBox
            ref={selectBoxRef}
            type="button"
            data-testid="sort-options"
            aria-controls={dropdownOpened ? dropdownId : undefined}
            aria-expanded={dropdownOpened}
            aria-haspopup="listbox"
            onClick={() => {
              setDropdownOpened(!dropdownOpened);
              setOuterClicked(false);
              setOuterEnterKeyPressed(false);
            }}
            onKeyDown={handleSelectBoxKeyDown}
          >
            {selectedSortOption && currentRefinement !== primaryIndex
              ? selectedSortOption.label
              : sortByLabel}

            {dropdownOpened ? (
              <CaretUpIcon weight="bold" aria-hidden="true" />
            ) : (
              <CaretDownIcon weight="bold" aria-hidden="true" />
            )}
          </S.SortBySelectBox>
          {dropdownOpened && (
            <S.SortByDropDown
              ref={dropdownRef}
              id={dropdownId}
              role="listbox"
              aria-label={sortByLabel}
              onKeyDown={handleDropdownKeyDown}
            >
              {sortingOptions.map(option => (
                <S.SortByItem
                  key={option.value}
                  type="button"
                  role="option"
                  aria-selected={option.value === currentRefinement}
                  $selected={option.value === currentRefinement}
                  data-testid={`sort-option-${option.suffix}`}
                  onClick={() => {
                    const { value, labelEN } = option;

                    handleSortingOptionClick(value, labelEN);
                  }}
                >
                  {option.label}
                </S.SortByItem>
              ))}
            </S.SortByDropDown>
          )}
        </S.ItemWrapper>
      </S.SortingOptionWrapper>
    </Skeleton>
  );
};
